'use client';

import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import Icon from '../icon';
import type { SplitPageHeroHighlight } from './types';

type HighlightsProps = {
	highlights?: SplitPageHeroHighlight[];
	className?: string;
};

export function SplitPageHeroHighlights({ highlights, className }: HighlightsProps) {
	// BL AC: "a minimum 3 and maximum 4 tiles" — UI AC 4a: "Minimum 3, maximum 4
	// for Curated". Below the minimum the section does not render.
	if (!highlights || highlights.length < 3) return null;

	// BL AC: "If more than 4 tiles are configured, only the first 4 tiles are to
	// be displayed. Any tiles beyond the 4th position will not be displayed on page."
	const visibleHighlights = highlights.slice(0, 4);
	// UI AC 4e: "When there are more than 3 Highlights shown on Mobile,
	// Highlights should show in a grid format".
	const useGrid = visibleHighlights.length > 3;

	return (
		// Three Figma-verified gaps (Split Page Hero "Highlight Bubbles" desktop
		// 1440 / mobile 375 frames), none of them the flat values this used to
		// have: the hr-to-row rhythm is 20/30 (stack-l, exact match), the tile
		// row's own gap is 15/20 (inline-m, exact match), and each tile's own
		// icon-to-label gap is bound to stack-xs (6/8/10, confirmed in Dev Mode
		// — inline-xs happens to share the same curve, but stack-xs is the
		// real token, not a fallback).
		<div className={cn('flex flex-col gap-y-stack-l', className)}>
			<hr className="w-full border-t" style={{ borderColor: 'rgba(77, 77, 77, 0.15)' }} />
			<div
				className={cn(
					'md:h-[95px] md:items-center',
					useGrid
						? 'grid grid-cols-2 gap-inline-m md:flex md:flex-nowrap'
						: 'flex flex-nowrap gap-inline-m'
				)}
			>
				{visibleHighlights.map((highlight, index) => (
					<div
						key={index}
						className="flex h-full flex-1 basis-0 flex-col items-center gap-y-stack-xs"
					>
						<div
							className="flex size-[64px] shrink-0 items-center justify-center"
							style={{ backgroundColor: 'rgba(203, 196, 188, 0.2)', borderRadius: '50%' }}
						>
							<Icon icon={highlight.icon} className="text-[32px]" />
						</div>
						<span className="text-caption text-text-primary text-center">
							<BevSafeText>{highlight.label}</BevSafeText>
						</span>
					</div>
				))}
			</div>
			<hr className="w-full border-t" style={{ borderColor: 'rgba(77, 77, 77, 0.15)' }} />
		</div>
	);
}
